import axios, { AxiosHeaders, isAxiosError } from "axios";
import { auth } from "../app/auth/auth";
import type { InvoiceDocumentType } from "../app/orders/invoiceDocumentType";
import type { CalculationListItem, CalculationListResponse, Note } from "@przezmorze/api-types";

export type NoteDTO = Note;

export interface FileDTO {
  name: string;
  url: string;
  visibility: "PRIVATE" | "PUBLIC";
}

export type ListCalculationsResponseItem = CalculationListItem;

export interface AttachmentResponse {
  readonly name: string;
  readonly url: string;
  readonly visibility: "PUBLIC" | "PRIVATE";
}

export interface InvoiceResponse {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly type?: InvoiceDocumentType;
}

export interface InvoicesResponse {
  readonly items: InvoiceResponse[];
}

export function throwMessageOnBadRequestStatus(
  error: unknown,
  extractMessage: (data: unknown) => string | undefined = extractMessageFromErrorResponseBody,
): never {
  throwMessageOnStatus(error, 400, "Invalid request", extractMessage);
}

export function throwMessageOnStatus(
  error: unknown,
  status: number,
  defaultMessage: string,
  extractMessage: (data: unknown) => string | undefined = extractMessageFromErrorResponseBody,
): never {
  throwMessageOnStatuses(error, [status], defaultMessage, extractMessage);
}

export function throwMessageOnStatuses(
  error: unknown,
  statuses: number[],
  defaultMessage: string = "Invalid request",
  extractMessage: (data: unknown) => string | undefined = extractMessageFromErrorResponseBody,
): never {
  if (isAxiosError(error) && statuses.includes(error.response?.status ?? -1)) {
    throw new Error(extractMessage(error.response?.data) ?? defaultMessage);
  } else {
    throw error;
  }
}

export function extractMessageFromErrorResponseBody(responseBody: unknown): string | undefined {
  if (typeof responseBody === "object" && responseBody && "message" in responseBody) {
    return responseBody.message as string;
  }
  return undefined;
}

export const przezmorzeApi = axios.create({
  baseURL: "/api",
  timeout: 45 * 1000, // 45 seconds
});

przezmorzeApi.interceptors.request.use(async (config) => {
  const token = await auth.getToken();
  config.headers = AxiosHeaders.from(config.headers ?? {});
  config.headers.setAuthorization(`Bearer ${token}`);
  return config;
});

export const przezmorzeBatchApi = axios.create({
  baseURL: "/batchapi",
  timeout: 4 * 60 * 1000, // 4 min
});

przezmorzeBatchApi.interceptors.request.use(async (config) => {
  const token = await auth.getToken();
  config.headers.setAuthorization(`Bearer ${token}`);
  return config;
});

// --- Orders ---

export async function getOrderPdf(
  orderId: string,
  includeOptionPrice: boolean,
  abortSignal?: AbortSignal,
): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(`/order-export/${orderId}/pdf`, {
    signal: abortSignal,
    headers: { Accept: "application/pdf" },
    params: includeOptionPrice ? { optionPrices: true } : undefined,
    responseType: "blob",
  });
  return response.data;
}

export type Department = "MARITIME_TRANSPORT" | "HOTEL" | "ATTRACTION";

// --- Order invoices ---

export type AddAdvanceOrProformaInvoiceRequestBody = {
  readonly value: number;
  readonly paymentDays: number;
  readonly sendByEmail: boolean;
  readonly includeOrderNameInDescription: boolean;
  readonly department?: Department;
} & (
  | {
      readonly paid: false;
    }
  | {
      readonly paid: true;
      readonly paymentDate: string;
    }
);

export async function addOrderAdvanceInvoice(
  orderId: string,
  body: AddAdvanceOrProformaInvoiceRequestBody,
  abortSignal?: AbortSignal,
): Promise<InvoiceResponse> {
  try {
    const response = await przezmorzeApi.post<InvoiceResponse>(`/invoices/${orderId}/advance-invoices`, body, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function addOrderProformaInvoice(
  orderId: string,
  body: AddAdvanceOrProformaInvoiceRequestBody,
  abortSignal?: AbortSignal,
): Promise<InvoiceResponse[]> {
  try {
    const response = await przezmorzeApi.post<InvoicesResponse>(`/invoices/${orderId}/proforma-invoices`, body, {
      signal: abortSignal,
    });
    return response.data.items;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export type CorrectionScope = {
  readonly departments?: Department[];
};

export type AddAdvanceCorrectionInvoiceRequestBody = {
  readonly sendByEmail: boolean;
} & CorrectionScope;

export async function addOrderAdvanceCorrectionInvoice(
  orderId: string,
  body: AddAdvanceCorrectionInvoiceRequestBody,
  abortSignal?: AbortSignal,
): Promise<InvoiceResponse[]> {
  try {
    const response = await przezmorzeApi.post<InvoicesResponse>(
      `/invoices/${orderId}/advance-correction-invoices`,
      body,
      {
        signal: abortSignal,
      },
    );
    return response.data.items;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export type AddFinalCorrectionInvoiceRequestBody = {
  readonly reason: string;
  readonly sendByEmail: boolean;
} & CorrectionScope;

export async function addOrderFinalCorrectionInvoice(
  orderId: string,
  body: AddFinalCorrectionInvoiceRequestBody,
  abortSignal?: AbortSignal,
): Promise<InvoiceResponse[]> {
  try {
    const response = await przezmorzeApi.post<InvoicesResponse>(
      `/invoices/${orderId}/final-correction-invoices`,
      body,
      {
        signal: abortSignal,
      },
    );
    return response.data.items;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export type FinalInvoiceDepartmentPaymentRequest =
  { readonly paid: false } | { readonly paid: true; readonly paymentDate: string } | { readonly paid: number };

export interface AddFinalInvoiceRequestBody {
  readonly paymentDays: number;
  readonly sendByEmail: boolean;
  readonly includeOrderNameInDescription: boolean;
  readonly paymentsByDepartment: Partial<Record<Department, FinalInvoiceDepartmentPaymentRequest>>;
}

export async function addOrderFinalInvoice(
  orderId: string,
  body: AddFinalInvoiceRequestBody,
  abortSignal?: AbortSignal,
): Promise<InvoiceResponse[]> {
  try {
    const response = await przezmorzeApi.post<InvoicesResponse>(`/invoices/${orderId}/final-invoices`, body, {
      signal: abortSignal,
    });
    return response.data.items;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function deleteInvoice(
  orderId: string,
  fileId: string,
  abortSignal?: AbortSignal,
): Promise<InvoiceResponse[]> {
  try {
    const response = await przezmorzeApi.delete<InvoicesResponse>(`/invoices/${orderId}/${fileId}`, {
      signal: abortSignal,
    });
    return response.data.items;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

/** Fetches an invoice PDF from the path an order invoice carries (e.g. `magico.pdfUrl`). */
export async function getInvoicePdf(pdfUrl: string, abortSignal?: AbortSignal): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(pdfUrl, {
    signal: abortSignal,
    headers: { Accept: "application/pdf" },
    responseType: "blob",
  });
  return response.data;
}

// --- Calculations ---

export async function getCalculationPdf(
  calculationId: string,
  version: number,
  includeOptionPrice: boolean,
  abortSignal?: AbortSignal,
): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(`/calculations/${calculationId}/pdf`, {
    signal: abortSignal,
    headers: { Accept: "application/pdf" },
    params: { optionPrices: includeOptionPrice, version },
    responseType: "blob",
  });
  return response.data;
}

export async function getCalculationPriceList(
  calculationId: string,
  year: number,
  version: number,
  abortSignal?: AbortSignal,
): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(`/calculations/${calculationId}/price-list-pdf`, {
    signal: abortSignal,
    headers: { Accept: "application/pdf" },
    params: { year, version },
    responseType: "blob",
  });
  return response.data;
}

export async function listCalculations(
  params: { from: string; to?: string },
  abortSignal?: AbortSignal,
): Promise<ListCalculationsResponseItem[]> {
  const response = await przezmorzeApi.get<CalculationListResponse>("/calculations", {
    signal: abortSignal,
    params,
  });
  return response.data.items;
}

export async function updateCalculationNotes(
  calculationId: string,
  notes: NoteDTO[],
  abortSignal?: AbortSignal,
): Promise<void> {
  await przezmorzeApi.put(`/calculations/${calculationId}/notes`, { notes }, { signal: abortSignal });
}
